import type { ReactNode } from 'react';

import type { ColorName, Style } from './buttonClasses';
import type { FilterSelectProps, SelectOptionGroup } from './FilterSelect';
import type { SelectOption } from './Select';
import type { Size, UpdateStatus } from './types';
import { General, Maps } from '../StrokeIcons';
import Button from './Button';
import ButtonGroup from './ButtonGroup';
import FilterSelect from './FilterSelect';

export const TEMPLATE_ALL_OPTION_VALUE = '';
export const TEMPLATE_ANY_OPTION_VALUE = '__ANY_CAMPAIGN__';

export type CampaignTemplateOption = {
  id: string;
  name: string;
  isMine?: boolean;
  errorCount?: number;
  ownerName?: string | null;
  ownerProfilePicture?: string | null;
};

export type CampaignTemplateFilterSelectProps = {
  templates: CampaignTemplateOption[];
  renderOwnerIcon?: (template: CampaignTemplateOption) => ReactNode;
  value?: string;
  /** The default/reset value. When provided and value !== defaultValue, shows X mark to clear. */
  defaultValue?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  /** Status for async operations: 'updating' shows spinner, 'success' shows checkmark, 'error' shows warning. */
  status?: UpdateStatus;
  includeRemoveOption?: boolean;
  /** Include "All Campaigns" and "Any Campaign" filter options at the top */
  includeFilterOptions?: boolean;
  size?: Size;
  color?: ColorName;
  tone?: Style;
  /** Custom clear handler. When provided with defaultValue, called instead of onChange(defaultValue). */
  onClear?: () => void;
  /** Where the popover opens; set 'bottom-end' when the trigger sits at the right edge. Default: below, left-aligned. */
  popoverPlacement?: FilterSelectProps['popoverPlacement'];
};

export default function CampaignTemplateFilterSelect({
  templates,
  renderOwnerIcon,
  value,
  defaultValue,
  onChange,
  placeholder = 'Select campaign',
  className,
  status,
  includeRemoveOption,
  includeFilterOptions,
  size,
  color,
  tone,
  onClear,
  popoverPlacement,
}: CampaignTemplateFilterSelectProps) {
  const selectedTemplate = templates.find((t) => t.id === value);
  const showClearButton = defaultValue !== undefined && value !== defaultValue;

  const toSelectOption = (template: CampaignTemplateOption): SelectOption => {
    const hasErrors = (template.errorCount ?? 0) > 0;
    const errorDescription = hasErrors
      ? `Campaign has ${template.errorCount} ${template.errorCount === 1 ? 'error' : 'errors'}`
      : undefined;

    return {
      value: template.id,
      label: template.name,
      icon: hasErrors ? (
        <General.HelpCircle className="text-fair-pink-600" />
      ) : (
        (renderOwnerIcon?.(template) ?? <Maps.Rocket />)
      ),
      description: errorDescription,
    };
  };

  const myTemplates = templates.filter((t) => t.isMine);
  const teamTemplates = templates.filter((t) => !t.isMine);

  const groups: (SelectOption | SelectOptionGroup)[] = [];

  if (includeRemoveOption) {
    groups.push({ value: '', label: 'Remove from campaign', icon: <General.Trash01 />, color: 'red' as const });
  }

  if (includeFilterOptions) {
    groups.push({ value: TEMPLATE_ALL_OPTION_VALUE, label: 'All Campaigns', icon: <Maps.Rocket /> });
    groups.push({ value: TEMPLATE_ANY_OPTION_VALUE, label: 'Any Campaign', icon: <Maps.Rocket /> });
  }

  if (myTemplates.length > 0) {
    groups.push({
      label: 'My Campaigns',
      options: myTemplates.map(toSelectOption),
    });
  }

  if (teamTemplates.length > 0) {
    groups.push({
      label: 'Team Campaigns',
      options: teamTemplates.map(toSelectOption),
    });
  }

  // Determine icon for trigger button
  const isFilterOption = value === TEMPLATE_ALL_OPTION_VALUE || value === TEMPLATE_ANY_OPTION_VALUE || value == null;
  const triggerIcon = isFilterOption ? (
    <Maps.Rocket />
  ) : selectedTemplate ? (
    (renderOwnerIcon?.(selectedTemplate) ?? <Maps.Rocket />)
  ) : (
    <Maps.Rocket />
  );

  const select = (
    <FilterSelect
      icon={triggerIcon}
      options={groups}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={className}
      status={status}
      autoWidth
      popoverPlacement={popoverPlacement}
      size={size}
      color={color}
      tone={tone}
    />
  );

  if (showClearButton) {
    return (
      <ButtonGroup size={size}>
        {select}
        <Button icon={<General.XMark />} onClick={onClear ?? (() => onChange(defaultValue))} />
      </ButtonGroup>
    );
  }

  return select;
}
